import React, { useState, useMemo, useEffect } from 'react';
import './BurndownChart.css';

export default function BurndownChart({ sprint, sprintTasks, planningMethod = 'hours', currentUserId = null, hasAdminAccess = false }) {
  // If user doesn't have admin access, default to their own ID; otherwise default to 'all'
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(
    hasAdminAccess ? 'all' : (currentUserId ? String(currentUserId) : 'all')
  );
  
  // Update selectedAssigneeId if user doesn't have admin access and currentUserId changes
  useEffect(() => {
    if (!hasAdminAccess && currentUserId) {
      setSelectedAssigneeId(String(currentUserId));
    }
  }, [hasAdminAccess, currentUserId]);
  // Calculate burndown data
  const burndownData = useMemo(() => {
    if (!sprint || !sprint.start_date || !sprint.end_date) {
      return null;
    }

    // Parse dates correctly - handle both Date objects and string formats
    const parseDate = (dateValue) => {
      if (!dateValue) return null;
      if (dateValue instanceof Date) {
        // If it's already a Date object, validate it
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }
      // Handle YYYY-MM-DD format (MySQL DATE format)
      if (typeof dateValue === 'string') {
        // Remove any time portion if present and add local timezone
        const dateOnly = dateValue.split('T')[0].split(' ')[0];
        // Create date in local timezone to avoid timezone issues
        const [year, month, day] = dateOnly.split('-').map(Number);
        if (year && month && day) {
          const date = new Date(year, month - 1, day);
          // Validate the date
          if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
            return date;
          }
        }
        // Fallback: try standard parsing
        const parsed = new Date(dateOnly + 'T00:00:00');
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const startDate = parseDate(sprint.start_date);
    const endDate = parseDate(sprint.end_date);
    
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Invalid sprint dates:', { start_date: sprint.start_date, end_date: sprint.end_date });
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Ensure sprintTasks is an array
    const tasks = Array.isArray(sprintTasks) ? sprintTasks : [];

    // Determine effective assignee ID (force to current user if non-admin)
    const effectiveAssigneeId = !hasAdminAccess && currentUserId 
      ? String(currentUserId) 
      : selectedAssigneeId;

    // Filter tasks by selected assignee
    // Non-admin users can only see their own tasks
    let filteredTasks;
    if (effectiveAssigneeId === 'all') {
      filteredTasks = tasks;
    } else {
      const assigneeId = parseInt(effectiveAssigneeId, 10);
      filteredTasks = tasks.filter(st => {
        if (!st.assignees || !Array.isArray(st.assignees)) {
          return false;
        }
        return st.assignees.some(a => a.person_id === assigneeId);
      });
    }

    // Calculate total work (hours or points) for filtered tasks
    // When filtering by assignee, only count work assigned to that person
    // Include both main task and subtask work estimates
    const totalWork = filteredTasks.reduce((sum, st) => {
      let taskWork = 0;
      let subtaskWork = 0;
      
      if (effectiveAssigneeId === 'all') {
        // Count full task estimate
        if (planningMethod === 'hours') {
          taskWork = parseFloat(st.estimated_hours) || 0;
        } else {
          taskWork = parseInt(st.estimated_points) || 0;
        }
        
        // Count all subtask work
        if (st.subtasks && Array.isArray(st.subtasks)) {
          subtaskWork = st.subtasks.reduce((subSum, subtask) => {
            return subSum + (subtask.assignees || []).reduce((assigneeSum, assignee) => {
              if (planningMethod === 'hours') {
                return assigneeSum + (parseFloat(assignee.assigned_hours) || 0);
              } else {
                return assigneeSum + (parseInt(assignee.assigned_points) || 0);
              }
            }, 0);
          }, 0);
        }
      } else {
        // Count only this assignee's portion from main task
        const assigneeId = parseInt(effectiveAssigneeId, 10);
        const assignee = st.assignees?.find(a => a.person_id === assigneeId);
        if (assignee) {
          if (planningMethod === 'hours') {
            taskWork = parseFloat(assignee.assigned_hours) || 0;
          } else {
            taskWork = parseInt(assignee.assigned_points) || 0;
          }
        }
        
        // Count only this assignee's portion from subtasks
        if (st.subtasks && Array.isArray(st.subtasks)) {
          subtaskWork = st.subtasks.reduce((subSum, subtask) => {
            const subtaskAssignee = (subtask.assignees || []).find(a => a.person_id === assigneeId);
            if (subtaskAssignee) {
              if (planningMethod === 'hours') {
                return subSum + (parseFloat(subtaskAssignee.assigned_hours) || 0);
              } else {
                return subSum + (parseInt(subtaskAssignee.assigned_points) || 0);
              }
            }
            return subSum;
          }, 0);
        }
      }
      
      return sum + taskWork + subtaskWork;
    }, 0);

    // Calculate completed work (simplified - tasks with status "Complete" or "Done")
    // For now, we'll use a simple calculation based on task status
    // In a real implementation, you'd track completion dates
    // When filtering by assignee, only count work assigned to that person
    // Include both main task and subtask work estimates
    const completedWork = filteredTasks.reduce((sum, st) => {
      const task = st.task || {};
      let taskWork = 0;
      let subtaskWork = 0;
      
      // Check if main task is complete
      if (task.status === 'Complete' || task.status === 'Done' || task.status === 'Completed') {
        if (effectiveAssigneeId === 'all') {
          // Count full task estimate
          if (planningMethod === 'hours') {
            taskWork = parseFloat(st.estimated_hours) || 0;
          } else {
            taskWork = parseInt(st.estimated_points) || 0;
          }
        } else {
          // Count only this assignee's portion
          const assigneeId = parseInt(effectiveAssigneeId, 10);
          const assignee = st.assignees?.find(a => a.person_id === assigneeId);
          if (assignee) {
            if (planningMethod === 'hours') {
              taskWork = parseFloat(assignee.assigned_hours) || 0;
            } else {
              taskWork = parseInt(assignee.assigned_points) || 0;
            }
          }
        }
      }
      
      // Check completed subtasks
      if (st.subtasks && Array.isArray(st.subtasks)) {
        subtaskWork = st.subtasks.reduce((subSum, subtask) => {
          if (subtask.status === 'Complete' || subtask.status === 'Done' || subtask.status === 'Completed') {
            if (effectiveAssigneeId === 'all') {
              // Count all subtask assignee work
              return subSum + (subtask.assignees || []).reduce((assigneeSum, assignee) => {
                if (planningMethod === 'hours') {
                  return assigneeSum + (parseFloat(assignee.assigned_hours) || 0);
                } else {
                  return assigneeSum + (parseInt(assignee.assigned_points) || 0);
                }
              }, 0);
            } else {
              // Count only this assignee's portion
              const assigneeId = parseInt(effectiveAssigneeId, 10);
              const subtaskAssignee = (subtask.assignees || []).find(a => a.person_id === assigneeId);
              if (subtaskAssignee) {
                if (planningMethod === 'hours') {
                  return subSum + (parseFloat(subtaskAssignee.assigned_hours) || 0);
                } else {
                  return subSum + (parseInt(subtaskAssignee.assigned_points) || 0);
                }
              }
            }
          }
          return subSum;
        }, 0);
      }
      
      return sum + taskWork + subtaskWork;
    }, 0);

    const remainingWork = totalWork - completedWork;

    // Generate data points for each day
    const days = [];
    const currentDate = new Date(startDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const totalDays = Math.max(1, daysDiff); // Ensure at least 1 day
    const daysElapsed = Math.max(0, Math.min(totalDays, Math.ceil((today - startDate) / (1000 * 60 * 60 * 24))));

    for (let i = 0; i <= totalDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      // Ideal burndown: linear from totalWork to 0
      const idealRemaining = totalDays > 0 ? totalWork * (1 - i / totalDays) : totalWork;
      
      // Actual burndown: starts at totalWork and decreases as work is completed
      // For days in the past, show progress; for future days, show current remaining
      let actualRemaining;
      if (i === 0) {
        // Day 0: start with total work
        actualRemaining = totalWork;
      } else if (i <= daysElapsed) {
        // Past/current days: show progress from totalWork to current remainingWork
        // Linear interpolation from totalWork (day 0) to remainingWork (current day)
        const progressRatio = i / daysElapsed;
        actualRemaining = totalWork - (totalWork - remainingWork) * progressRatio;
      } else {
        // Future days: show current remaining work (assuming no more progress)
        actualRemaining = remainingWork;
      }

      days.push({
        day: i,
        date: date,
        ideal: Math.max(0, idealRemaining),
        actual: Math.max(0, actualRemaining)
      });
    }

    return {
      totalWork,
      completedWork,
      remainingWork,
      days,
      totalDays,
      daysElapsed,
      unit: planningMethod === 'hours' ? 'hours' : 'points'
    };
  }, [sprint, sprintTasks, planningMethod, selectedAssigneeId, hasAdminAccess, currentUserId]);

  // Get unique assignees for the dropdown (must be before early returns to maintain hook order)
  const allAssigneesForDropdown = useMemo(() => {
    const assigneeMap = new Map();
    (sprintTasks || []).forEach(st => {
      if (st.assignees && Array.isArray(st.assignees)) {
        st.assignees.forEach(assignee => {
          if (!assigneeMap.has(assignee.person_id)) {
            assigneeMap.set(assignee.person_id, {
              person_id: assignee.person_id,
              person_name: assignee.person_name,
              person_email: assignee.person_email
            });
          }
        });
      }
    });
    return Array.from(assigneeMap.values()).sort((a, b) => 
      a.person_name.localeCompare(b.person_name)
    );
  }, [sprintTasks]);

  if (!burndownData || !sprint || !burndownData.days || burndownData.days.length === 0) {
    return (
      <div className="burndown-chart-container">
        <div className="burndown-chart-empty">
          <p>No active sprint found or invalid sprint dates. Create and activate a sprint with valid start and end dates to view the burndown chart.</p>
        </div>
      </div>
    );
  }

  // Check if there are tasks but no work estimates
  const hasTasks = sprintTasks && sprintTasks.length > 0;
  const hasWorkEstimates = burndownData.totalWork > 0;
  
  if (hasTasks && !hasWorkEstimates) {
    return (
      <div className="burndown-chart-container">
        <div className="burndown-chart-empty">
          <p>
            <strong>No work estimates found for sprint tasks.</strong>
          </p>
          <p>
            Tasks have been added to the sprint, but they need estimated {planningMethod === 'hours' ? 'hours' : 'points'} to display in the burndown chart.
          </p>
          <p>To add estimates, you can:</p>
          <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '0.5rem' }}>
            <li>Edit tasks in the sprint and set estimated {planningMethod === 'hours' ? 'hours' : 'points'}</li>
            <li>Or use the Capacity Management modal to set estimates</li>
          </ul>
        </div>
      </div>
    );
  }

  const { totalWork, completedWork, remainingWork, days, totalDays, daysElapsed, unit } = burndownData;
  
  // Ensure we have valid days data
  if (!days || days.length === 0 || isNaN(totalDays) || isNaN(daysElapsed)) {
    return (
      <div className="burndown-chart-container">
        <div className="burndown-chart-empty">
          <p>Unable to calculate burndown data. Please check that the sprint has valid dates.</p>
        </div>
      </div>
    );
  }

  // Chart dimensions
  const chartWidth = 800;
  const chartHeight = 400;
  const padding = { top: 40, right: 40, bottom: 60, left: 80 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;

  // Calculate scales
  const maxWork = Math.max(totalWork * 1.1, 1); // Add 10% padding, ensure at least 1
  const yScale = maxWork > 0 ? plotHeight / maxWork : plotHeight;
  const xScale = totalDays > 0 ? plotWidth / totalDays : plotWidth;

  // Generate path for ideal line
  // Note: SVG Y-axis is inverted (0 at top), so we subtract from plotHeight
  const idealPath = days.map((d, i) => {
    const x = padding.left + (d.day * xScale);
    const y = padding.top + plotHeight - (d.ideal * yScale);
    // Ensure x and y are valid numbers
    if (isNaN(x) || isNaN(y) || !isFinite(x) || !isFinite(y)) {
      return '';
    }
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).filter(segment => segment !== '').join(' ');

  // Generate path for actual line
  // Note: SVG Y-axis is inverted (0 at top), so we subtract from plotHeight
  const actualPath = days.slice(0, Math.max(1, daysElapsed + 1)).map((d, i) => {
    const x = padding.left + (d.day * xScale);
    const y = padding.top + plotHeight - (d.actual * yScale);
    // Ensure x and y are valid numbers
    if (isNaN(x) || isNaN(y) || !isFinite(x) || !isFinite(y)) {
      return '';
    }
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).filter(segment => segment !== '').join(' ');

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="burndown-chart-container">
      <div className="burndown-chart-header">
        <div className="burndown-chart-header-left">
          <h3>Burndown Chart: {sprint.name}</h3>
          {hasAdminAccess && allAssigneesForDropdown.length > 0 && (
            <div className="burndown-chart-filter">
              <label htmlFor="assignee-filter">Filter by:</label>
              <select
                id="assignee-filter"
                value={selectedAssigneeId}
                onChange={(e) => setSelectedAssigneeId(e.target.value)}
                className="burndown-chart-filter-select"
              >
                <option value="all">All Assignees</option>
                {allAssigneesForDropdown.map(assignee => (
                  <option key={assignee.person_id} value={assignee.person_id}>
                    {assignee.person_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!hasAdminAccess && currentUserId && (
            <div className="burndown-chart-filter">
              <span className="burndown-chart-filter-label">
                Showing: {allAssigneesForDropdown.find(a => a.person_id === currentUserId)?.person_name || 'Your work'}
              </span>
            </div>
          )}
        </div>
        <div className="burndown-chart-stats">
          <div className="stat">
            <span className="stat-label">Total Work:</span>
            <span className="stat-value">{totalWork.toFixed(1)} {unit}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Completed:</span>
            <span className="stat-value">{completedWork.toFixed(1)} {unit}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Remaining:</span>
            <span className="stat-value">{remainingWork.toFixed(1)} {unit}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Progress:</span>
            <span className="stat-value">{totalWork > 0 ? ((completedWork / totalWork) * 100).toFixed(1) : 0}%</span>
          </div>
        </div>
      </div>

      <div className="burndown-chart-legend">
        <div className="legend-item">
          <div className="legend-line ideal"></div>
          <span>Ideal Burndown</span>
        </div>
        <div className="legend-item">
          <div className="legend-line actual"></div>
          <span>Actual Burndown</span>
        </div>
      </div>

      <svg width={chartWidth} height={chartHeight} className="burndown-chart-svg">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          // Y-axis is inverted in SVG, so higher values are at the top
          // ratio 0 = top (maxWork), ratio 1 = bottom (0)
          const value = maxWork * (1 - ratio);
          const y = padding.top + (plotHeight * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + plotWidth}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="rgba(255, 255, 255, 0.6)"
                fontSize="12"
                textAnchor="end"
              >
                {value.toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* X-axis labels (every few days) */}
        {days.filter((d, i) => i % Math.max(1, Math.floor(totalDays / 8)) === 0 || i === totalDays).map((d) => {
          const x = padding.left + (d.day * xScale);
          return (
            <g key={d.day}>
              <line
                x1={x}
                y1={padding.top + plotHeight}
                x2={x}
                y2={padding.top + plotHeight + 5}
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth="1"
              />
              <text
                x={x}
                y={padding.top + plotHeight + 20}
                fill="rgba(255, 255, 255, 0.6)"
                fontSize="11"
                textAnchor="middle"
                transform={`rotate(-45 ${x} ${padding.top + plotHeight + 20})`}
              >
                {formatDate(d.date)}
              </text>
            </g>
          );
        })}

        {/* Ideal burndown line */}
        <path
          d={idealPath}
          fill="none"
          stroke="#4a9eff"
          strokeWidth="2"
          strokeDasharray="5,5"
          opacity="0.7"
        />

        {/* Actual burndown line */}
        <path
          d={actualPath}
          fill="none"
          stroke="#00ff88"
          strokeWidth="3"
        />

        {/* Current day indicator */}
        {daysElapsed >= 0 && daysElapsed <= totalDays && (
          <line
            x1={padding.left + (daysElapsed * xScale)}
            y1={padding.top}
            x2={padding.left + (daysElapsed * xScale)}
            y2={padding.top + plotHeight}
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth="2"
            strokeDasharray="3,3"
          />
        )}

        {/* Y-axis label */}
        <text
          x={20}
          y={padding.top + plotHeight / 2}
          fill="rgba(255, 255, 255, 0.8)"
          fontSize="14"
          textAnchor="middle"
          transform={`rotate(-90 20 ${padding.top + plotHeight / 2})`}
        >
          Remaining {unit.charAt(0).toUpperCase() + unit.slice(1)}
        </text>

        {/* X-axis label */}
        <text
          x={padding.left + plotWidth / 2}
          y={chartHeight - 10}
          fill="rgba(255, 255, 255, 0.8)"
          fontSize="14"
          textAnchor="middle"
        >
          Days
        </text>
      </svg>

      <div className="burndown-chart-info">
        <p>
          Sprint: {days[0]?.date ? formatDate(days[0].date) : 'N/A'} - {days[days.length - 1]?.date ? formatDate(days[days.length - 1].date) : 'N/A'}
          {' | '}
          Day {Math.max(1, daysElapsed + 1)} of {Math.max(1, totalDays + 1)}
        </p>
      </div>
    </div>
  );
}

